import { Assets } from 'pixi.js-legacy';
import { createPixiApp } from './renderer/app';
import { GameController } from './game/game-controller';
import { SoundManager } from './game/sound-manager';
import './style.css';

/**
 * Check if winter theme is enabled via URL parameter
 */
function isWinterThemeEnabled(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('winter') === '1';
}

/**
 * Get tile texture paths based on theme
 */
function getTileTextures(): string[] {
  const isWinter = isWinterThemeEnabled();
  const suffix = isWinter ? '_winter' : '';

  return [
    `./images/tile1${suffix}.png`,
    `./images/tile2${suffix}.png`,
    `./images/tile3${suffix}.png`,
    `./images/tile4${suffix}.png`,
    `./images/tile5${suffix}.png`,
  ];
}

/**
 * Get background image path based on theme
 */
function getBackgroundImage(): string {
  const isWinter = isWinterThemeEnabled();
  return isWinter ? './images/background_winter.jpg' : './images/background.jpg';
}

// Tile texture paths
const TILE_TEXTURES = getTileTextures();
const BACKGROUND_IMAGE = getBackgroundImage();


/**
 * Preload all game assets
 */
async function preloadAssets(): Promise<void> {
  try {
    // Load background image
    await Assets.load(BACKGROUND_IMAGE);

    // Add all tile textures to the loader
    for (let i = 0; i < TILE_TEXTURES.length; i++) {
      const texturePath = TILE_TEXTURES[i];
      await Assets.load(texturePath);
    }

    // Load sound files (non-blocking - don't fail if sounds missing)
    try {
      await SoundManager.preload('click', './sounds/click.mp3');
    } catch {
      console.warn('[Assets] ⚠ Failed to load click sound (optional)');
    }
    try {
      await SoundManager.preload('click_winter', './sounds/click_winter.mp3');
    } catch {
      console.warn('[Assets] ⚠ Failed to load winter click sound (optional)');
    }
    try {
      await SoundManager.preload('swoosh', './sounds/swoosh.mp3');
    } catch {
      console.warn('[Assets] ⚠ Failed to load swoosh sound (optional)');
    }
    try {
      await SoundManager.preload('swoosh_winter', './sounds/swoosh_winter.mp3');
    } catch {
      console.warn('[Assets] ⚠ Failed to load winter swoosh sound (optional)');
    }
    try {
      await SoundManager.preload('gameover', './sounds/gameover.mp3');
    } catch {
      console.warn('[Assets] ⚠ Failed to load gameover sound (optional)');
    }
  } catch (error) {
    console.error('[Assets] ERROR loading assets:', error);
    throw error;
  }
}

// Wait for DOM to be ready
document.addEventListener('DOMContentLoaded', async () => {
  try {
    const appContainer = document.getElementById('app');

    if (!appContainer) {
      throw new Error('App container not found');
    }

    // Create Pixi application
    const pixiApp = await createPixiApp(appContainer);
    Assets.init({ skipDetections: true });

    // Preload all assets
    await preloadAssets();

    // Initialize game controller
    new GameController(pixiApp);
  } catch (error) {
    console.error('[Init] FATAL ERROR during initialization:', error);
    console.error(
      '[Init] Error stack:',
      error instanceof Error ? error.stack : 'No stack trace'
    );

    // Display error to user
    const appContainer = document.getElementById('app');
    if (appContainer) {
      appContainer.innerHTML = `
        <div style="color: white; padding: 20px; font-family: monospace;">
          <h2>Game Failed to Load</h2>
          <p>An error occurred during initialization:</p>
          <pre style="background: #222; padding: 10px; border-radius: 4px;">${error}</pre>
          <p>Please check the console for more details.</p>
        </div>
      `;
    }
  }
});
